import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";

const AUDIT_LABELS = {
  "login.success":             { label: "Sign in",                tone: "ok"   },
  "login.failed.bad_password": { label: "Failed sign in",         tone: "warn" },
  "login.failed.ip_blocked":   { label: "IP blocked at sign in",  tone: "bad"  },
  "login.failed.pending":      { label: "Sign in (account pending)",     tone: "warn" },
  "login.failed.rejected":     { label: "Sign in (account rejected)",    tone: "bad"  },
  "password.admin_reset":      { label: "Admin reset user password",     tone: "warn" },
  "password.self_change":      { label: "Self password change",          tone: "ok"   },
  "password.self_change.failed": { label: "Self password change failed", tone: "warn" },
  "ip_restrictions.updated":   { label: "IP allow-list updated",         tone: "warn" },
  "login_page.updated":        { label: "Login page edited",             tone: "ok"   },
  "quote.estimator_mismatch":  { label: "Estimator name mismatch",       tone: "warn" },
  "cost_settings.updated":     { label: "Cost settings updated",         tone: "ok"   },
};
const TONE_CLASS = {
  ok:   "bg-emerald-50 border-emerald-100 text-emerald-700",
  warn: "bg-amber-50 border-amber-100 text-amber-700",
  bad:  "bg-red-50 border-red-100 text-red-700",
};

export function AuditLogTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [openRow, setOpenRow] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (group) params.set("group", group);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "200");
      const { data } = await api.get(`/audit-log?${params.toString()}`);
      setItems(data.items || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [group]);

  const GROUPS = [
    { k: "",          label: "All events" },
    { k: "auth",      label: "Sign-in" },
    { k: "password",  label: "Passwords" },
    { k: "ip",        label: "IP restrictions" },
    { k: "login_page", label: "Login page" },
    { k: "quotes",    label: "Quote integrity" },
  ];

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <div className="qb-card p-4 space-y-3">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Security audit log</div>
        <p className="text-sm text-slate-600">Every sensitive action — sign-ins, password resets, IP allow-list and login-page edits — is recorded here with the actor's email and the IP they connected from.</p>
        <div className="flex flex-wrap gap-2 items-center">
          {GROUPS.map((g) => (
            <button
              key={g.k || "all"}
              onClick={() => setGroup(g.k)}
              className={`px-3 h-8 rounded-full text-xs uppercase tracking-wider font-semibold border transition-colors ${group === g.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-brand-blue hover:text-brand-blue"}`}
              data-testid={`audit-group-${g.k || "all"}`}
            >
              {g.label}
            </button>
          ))}
          <div className="ml-auto flex gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Search email or IP…"
              className="qb-input font-mono text-xs h-8"
              data-testid="audit-search-input"
            />
            <button onClick={load} disabled={busy} className="h-8 px-3 rounded-full bg-slate-900 text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue disabled:opacity-60" data-testid="audit-refresh">{busy ? "…" : "Refresh"}</button>
          </div>
        </div>
      </div>

      <div className="qb-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-slate-50/60 border-b border-slate-100">
              {["When", "Event", "Actor", "IP", "Target / detail"].map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-slate-400 text-sm">No events match this filter.</td></tr>
            )}
            {items.map((e, i) => {
              const meta = AUDIT_LABELS[e.event] || { label: e.event, tone: "warn" };
              const isOpen = openRow === i;
              const detail = e.target_name || (e.details ? Object.entries(e.details).slice(0, 3).map(([k, v]) => `${k}=${Array.isArray(v) ? v.length : JSON.stringify(v)}`).join(" · ") : "");
              return (
                <React.Fragment key={i}>
                  <tr className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => setOpenRow(isOpen ? null : i)} data-testid={`audit-row-${i}`}>
                    <td className="px-4 py-2.5 text-xs text-slate-600 font-mono whitespace-nowrap">{fmtAuditAt(e.at)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${TONE_CLASS[meta.tone] || TONE_CLASS.warn}`}>{meta.label}</span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{e.actor_email || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{e.ip || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 truncate max-w-[320px]">{detail || "—"}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <td colSpan={5} className="px-4 py-3">
                        <pre className="text-[11px] text-slate-700 font-mono whitespace-pre-wrap break-all">{JSON.stringify({ event: e.event, target_id: e.target_id, target_name: e.target_name, details: e.details }, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function fmtAuditAt(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}
